import { DateTime } from "luxon";

import { ajaxController } from "./controller/AjaxController";
import { clientMutationOptions } from "./controller/clientMutation";
import { PagedResult } from "../DTOs/Common/PagedResult";
import { MisfireTreatment } from "../DTOs/apiEnums.generated";
import {
  BlastFiringRecordDTO,
  FieldPatternDTO,
  FiringDTO,
  FiringSaveResultDTO,
  OpenMisfireDTO,
  PatternFragmentationDTO,
  SaveBlastSentryRequest,
  SaveFragmentationRequest
} from "../DTOs/DrillBlast/FiringDTO";
import { PatternMonitoringDTO, SaveMonitorReadingRequest } from "../DTOs/DrillBlast/MonitoringDTO";

export interface ReportMisfireRequest {
  blastFiringId?: number | null;
  holeReference?: string | null;
  foundAt: DateTime;
  foundByPersonId?: number | null;
  suspectedCause?: string | null;
}

class BlastFiringAjax {
  private _ajaxController = ajaxController;
  private _endpoint = "/BlastFiring";

  public getRecord(patternBlastId: number) {
    return this._ajaxController.get<BlastFiringRecordDTO>(`${this._endpoint}/${patternBlastId}`, {});
  }

  public getOpenMisfires() {
    return this._ajaxController.get<PagedResult<OpenMisfireDTO>>(`${this._endpoint}/open-misfires`, {});
  }

  public getFieldPatterns() {
    return this._ajaxController.get<PagedResult<number>>(`${this._endpoint}/field-patterns`, {});
  }

  public getPatterns() {
    return this._ajaxController.get<PagedResult<FieldPatternDTO>>(`${this._endpoint}/patterns`, {});
  }

  public saveFiring(patternBlastId: number, firing: FiringDTO, acknowledge: boolean, clientId?: string) {
    return this._ajaxController.post<{ firing: FiringDTO; acknowledge: boolean }, FiringSaveResultDTO>(
      `${this._endpoint}/${patternBlastId}/firings`, { firing, acknowledge }, clientMutationOptions(clientId));
  }

  public reportMisfire(patternBlastId: number, request: ReportMisfireRequest, clientId?: string) {
    return this._ajaxController.post<ReportMisfireRequest, BlastFiringRecordDTO>(
      `${this._endpoint}/${patternBlastId}/misfires`, request, clientMutationOptions(clientId));
  }

  public clearMisfire(misfireId: number, request: { clearedAt: DateTime; treatment: MisfireTreatment; clearedByPersonId?: number | null; rowVersion?: string | null }) {
    return this._ajaxController.post<typeof request, BlastFiringRecordDTO>(`${this._endpoint}/misfires/${misfireId}/clear`, request);
  }

  public saveSentry(patternBlastId: number, firingId: number, request: SaveBlastSentryRequest) {
    return this._ajaxController.post<SaveBlastSentryRequest, BlastFiringRecordDTO>(`${this._endpoint}/${patternBlastId}/firings/${firingId}/sentries`, request);
  }

  public removeSentry(sentryId: number) {
    return this._ajaxController.delete<BlastFiringRecordDTO>(`${this._endpoint}/sentries/${sentryId}`);
  }

  public getClearanceSheet(patternBlastId: number, firingId: number) {
    return this._ajaxController.get<string>(`${this._endpoint}/${patternBlastId}/firings/${firingId}/clearance-sheet`, {});
  }

  public getFragmentation(patternBlastId: number) {
    return this._ajaxController.get<PatternFragmentationDTO>(`${this._endpoint}/${patternBlastId}/fragmentation`, {});
  }

  public saveFragmentation(patternBlastId: number, request: SaveFragmentationRequest) {
    return this._ajaxController.post<SaveFragmentationRequest, PatternFragmentationDTO>(`${this._endpoint}/${patternBlastId}/fragmentation`, request);
  }

  public removeFragmentation(fragmentationId: number) {
    return this._ajaxController.delete<PatternFragmentationDTO>(`${this._endpoint}/fragmentation/${fragmentationId}`);
  }

  public getMonitoring(patternBlastId: number) {
    return this._ajaxController.get<PatternMonitoringDTO>(`${this._endpoint}/${patternBlastId}/monitoring`, {});
  }

  public saveReading(patternBlastId: number, request: SaveMonitorReadingRequest) {
    return this._ajaxController.post<SaveMonitorReadingRequest, PatternMonitoringDTO>(`${this._endpoint}/${patternBlastId}/readings`, request);
  }
}

export const blastFiringAjax = new BlastFiringAjax();
